"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CheckboxField = void 0;
const joi_1 = __importDefault(require("joi"));
const field_1 = require("./field");
const view_types_1 = require("../../types/view_types");
const field_types_1 = require("../../types/field_types");
const datasheet_1 = require("../datasheet");
const lodash_1 = require("lodash");
const stat_1 = require("./stat");
const i18n_1 = require("../../exports/i18n");
const utils_1 = require("../../model/utils");
const emojis_config_1 = require("../../config/emojis_config");
const validate_schema_1 = require("./validate_schema");
const trueText = ['1', 'true', (0, i18n_1.t)(i18n_1.Strings.stat_checked)];
const falseText = ['0', 'false', (0, i18n_1.t)(i18n_1.Strings.stat_un_checked)];
class CheckboxField extends field_1.Field {
    get apiMetaProperty() {
        return {
            icon: (0, utils_1.getEmojiIconNativeString)(this.field.property.icon),
        };
    }
    get openValueJsonSchema() {
        return {
            title: this.field.name,
            type: 'boolean',
        };
    }
    get statTypeList() {
        return CheckboxField._statTypeList;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.Boolean;
    }
    get acceptFilterOperators() {
        return CheckboxField._acceptFilterOperators;
    }
    static _isMeetFilter(operator, cellValue, conditionValue) {
        switch (operator) {
            case view_types_1.FOperator.Is:
                return Boolean(conditionValue) === Boolean(cellValue);
            default: {
                console.warn('Method should be overwrite!');
                return true;
            }
        }
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        return CheckboxField._isMeetFilter(operator, cellValue, conditionValue);
    }
    static defaultProperty() {
        return {
            icon: 'white_check_mark',
        };
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.Checkbox,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    validateProperty() {
        return CheckboxField.propertySchema.validate(this.field.property);
    }
    validateCellValue(cv) {
        return CheckboxField.cellValueSchema.validate(cv);
    }
    validateOpenWriteValue(owv) {
        return CheckboxField.openWriteValueValueSchema.validate(owv);
    }
    cellValueToStdValue(cellValue) {
        return {
            sourceType: this.field.type,
            data: [{
                    text: this.cellValueToString(cellValue) || '',
                }],
        };
    }
    stdValueToCellValue(stdValue) {
        if ((0, lodash_1.isEmpty)(stdValue) || (0, lodash_1.isEmpty)(stdValue.data)) {
            return null;
        }
        if (trueText.includes(stdValue.data[0].text)) {
            return true;
        }
        if (falseText.includes(stdValue.data[0].text)) {
            return null;
        }
        if (stdValue.data[0].text) {
            return true;
        }
        return null;
    }
    validate(value) {
        if (value == null || typeof value === 'boolean') {
            return true;
        }
        return false;
    }
    cellValueToString(cellValue) {
        return cellValue ? '1' : '0';
    }
    defaultValueForCondition(_condition) {
        return true;
    }
    static _compare(cellValue1, cellValue2, _orderInCellValueSensitive) {
        return Boolean(cellValue1) === Boolean(cellValue2) ? 0 : (Boolean(cellValue1) > Boolean(cellValue2) ? 1 : -1);
    }
    compare(cellValue1, cellValue2, orderInCellValueSensitive) {
        return CheckboxField._compare(cellValue1, cellValue2, orderInCellValueSensitive);
    }
    cellValueToApiStandardValue(cellValue) {
        if (cellValue === true) {
            return true;
        }
        return null;
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        if (cellValue === true) {
            return true;
        }
        return null;
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        return openWriteValue;
    }
    get openFieldProperty() {
        return {
            icon: (0, utils_1.getEmojiIconNativeString)(this.field.property.icon),
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        const result = CheckboxField.propertySchema.validate(updateProperty);
        if (!result.error && !emojis_config_1.EmojisConfig[updateProperty.icon]) {
            return (0, validate_schema_1.joiErrorResult)('icon is not Emoji slug');
        }
        return result;
    }
    static _filterValueToOpenFilterValue(value) {
        return value;
    }
    filterValueToOpenFilterValue(value) {
        return CheckboxField._filterValueToOpenFilterValue(value);
    }
    static _openFilterValueToFilterValue(value) {
        return value;
    }
    openFilterValueToFilterValue(value) {
        return CheckboxField._openFilterValueToFilterValue(value);
    }
    static _validateOpenFilterValue(value) {
        return CheckboxField.validateOpenFilterSchema.validate(value);
    }
    validateOpenFilterValue(value) {
        return CheckboxField._validateOpenFilterValue(value);
    }
}
exports.CheckboxField = CheckboxField;
CheckboxField.propertySchema = joi_1.default.object({
    icon: joi_1.default.string().required(),
}).required();
CheckboxField.cellValueSchema = joi_1.default.boolean().allow(null).required();
CheckboxField.openWriteValueValueSchema = joi_1.default.boolean().allow(null).required();
CheckboxField._statTypeList = [
    stat_1.StatType.None,
    stat_1.StatType.CountAll,
    stat_1.StatType.Checked,
    stat_1.StatType.UnChecked,
    stat_1.StatType.PercentChecked,
    stat_1.StatType.PercentUnChecked,
];
CheckboxField._acceptFilterOperators = [view_types_1.FOperator.Is];
CheckboxField.validateOpenFilterSchema = joi_1.default.boolean().allow(null);
//# sourceMappingURL=checkbox_field.js.map